import {
  CopyOutlined,
  DeleteOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined
} from "@ant-design/icons"
import { Button, Dropdown, Flex, type MenuProps, Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { ReportExportMenu } from "./ReportExportMenu.tsx"
import { ReportShareScheduleActions } from "./ReportShareScheduleActions.tsx"

interface ReportToolbarProps {
  reportId: string
  reportName: string
  saving?: boolean
  isFullscreen: boolean
  canEdit: boolean
  canDelete: boolean
  canShare: boolean
  canSchedule: boolean
  onRefresh: () => void
  onSave: () => void
  onAddWidget: () => void
  onDuplicate: () => void
  onDelete: () => void
  onToggleFullscreen: () => void
  onShare: () => void
  onSchedule: () => void
  onPrint: () => void
}

export const ReportToolbar = ({
  reportId,
  reportName,
  saving,
  isFullscreen,
  canEdit,
  canDelete,
  canShare,
  canSchedule,
  onRefresh,
  onSave,
  onAddWidget,
  onDuplicate,
  onDelete,
  onToggleFullscreen,
  onShare,
  onSchedule,
  onPrint
}: ReportToolbarProps) => {
  const { t } = useTranslation()

  const menuItems: MenuProps["items"] = [
    {
      key: "duplicate",
      icon: <CopyOutlined />,
      label: t("reports.toolbar.duplicate"),
      onClick: onDuplicate
    },
    {
      key: "fullscreen",
      icon: isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />,
      label: isFullscreen
        ? t("reports.toolbar.exitFullscreen")
        : t("reports.toolbar.fullscreen"),
      onClick: onToggleFullscreen
    },
    ...(canDelete
      ? [
          {
            key: "delete",
            icon: <DeleteOutlined />,
            danger: true,
            label: t("reports.toolbar.delete"),
            onClick: onDelete
          }
        ]
      : [])
  ]

  return (
    <Flex align="center" gap="small" wrap="wrap" className="reports-no-print">
      <Tooltip title={t("reports.toolbar.refresh")}>
        <Button
          icon={<ReloadOutlined />}
          aria-label={t("reports.toolbar.refresh")}
          onClick={onRefresh}
        />
      </Tooltip>

      {canEdit && (
        <Button icon={<SaveOutlined />} loading={saving} onClick={onSave}>
          {t("reports.toolbar.save")}
        </Button>
      )}

      <ReportExportMenu
        reportId={reportId}
        reportName={reportName}
        onPrint={onPrint}
      />

      <ReportShareScheduleActions
        canShare={canShare}
        canSchedule={canSchedule}
        onShare={onShare}
        onSchedule={onSchedule}
      />

      {canEdit && (
        <Button type="primary" icon={<PlusOutlined />} onClick={onAddWidget}>
          {t("reports.detail.addWidget")}
        </Button>
      )}

      <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
        <Button icon={<MoreOutlined />} aria-label={t("reports.toolbar.more")} />
      </Dropdown>
    </Flex>
  )
}
